import UrlContext from '@frontastic/catwalk/js/app/urlContext';
import Router from './router';
import { sdk } from '../../../../sdk';
import { FrontasticStore } from '../../../redux/store';

type Parameters = object & {
  hasError?: boolean;
  ownErrorHandler?: boolean;
  [key: string]: string | boolean | number | undefined;
};

type Body = any;
type SuccessCallback = (result: any, parameters: Parameters) => void;
type ErrorCallback = (error: any) => void;

interface RefreshInterval {
  refreshFunction: () => void;
  refreshInterval: number;
  interval: number | null;
}

/**
 * This is a copy of frontasticBridge/catwalk/js/app/api that uses direct calls to the Extension Hub
 * instead of proxy calls
 */
class DirectApi {
  public router: Router;
  private store: FrontasticStore;
  private intervals: RefreshInterval[] = [];

  static readonly Endpoints = {
    'Frontastic.WishlistApi.Wishlist.get': 'wishlist/getWishlist',
  } as const;

  constructor(router: Router, store: FrontasticStore) {
    this.router = router;
    this.store = store;
    this.configureSdk(router.context.locale);
  }

  private configureSdk(locale: string) {
    sdk.defaultConfigure(locale);
  }

  async request(
    method: string,
    route: string,
    parameters: Parameters = {},
    body: Body = undefined,
    successCallback: SuccessCallback | undefined = undefined,
    errorCallback: ErrorCallback | undefined = undefined,
  ): Promise<any> {
    try {
      const actionName = DirectApi.Endpoints[route];

      try {
        const response = await sdk.callAction({
          actionName,
          payload: {
            ...parameters,
          },
        });

        if (response.isError) {
          throw new Error('Failed to fetch wishlist');
        }
        // @TODO: check if really need this code
        // this.handleFrontasticRedirect(response);

        if (successCallback) {
          successCallback(response, parameters);
        }

        return response;
      } catch (error) {
        console.error('Error:', error);
      }
    } catch (error) {
      if (errorCallback) {
        errorCallback(error);
      }

      if (!parameters?.hasError && !parameters?.ownErrorHandler) {
        // @COFIXME[not-implemented](FLBML-142): Handle error
      } else if (!errorCallback) {
        throw error;
      }
    }
  }

  requestContinuosly(
    method: string,
    route: string,
    parameters: Parameters = {},
    successCallback: SuccessCallback | undefined = undefined,
    errorCallback: ErrorCallback | undefined = undefined,
  ): void {
    const refreshInterval = this.getReloadInterval();
    const refreshFunction = () => {
      this.request(method, route, parameters, null, successCallback, errorCallback);
    };

    refreshFunction();
    this.intervals.push({
      refreshFunction,
      refreshInterval,
      interval: window.setInterval(refreshFunction, refreshInterval),
    });
  }

  async trigger(route: string, parameters: Parameters = {}, actionId: string | null = null) {
    const routeKey = this.getTriggerRouteKey(route);

    return this.request(
      'GET',
      route,
      parameters,
      null,
      this.getTriggerSuccessFunction(routeKey, actionId, parameters),
      this.getTriggerErrorFunction(routeKey, actionId, parameters),
    );
  }

  triggerContinuously(route: string, parameters: Parameters = {}, actionId: string | null = null): void {
    const routeKey = this.getTriggerRouteKey(route);

    this.requestContinuosly(
      'GET',
      route,
      parameters,
      this.getTriggerSuccessFunction(routeKey, actionId, parameters),
      this.getTriggerErrorFunction(routeKey, actionId, parameters),
    );
  }

  clearContinuousRequests() {
    this.pauseRequests();
    this.intervals = [];
  }

  pauseRequests() {
    if (!this.intervals) {
      return;
    }

    for (let i = 0; i < this.intervals.length; ++i) {
      const interval = this.intervals[i].interval;
      if (interval !== null) {
        window.clearInterval(interval);
      }
      this.intervals[i].interval = null;
    }
  }

  resumeRequests() {
    if (!this.intervals) {
      return;
    }

    for (let i = 0; i < this.intervals.length; ++i) {
      this.intervals[i].interval = window.setInterval(
        this.intervals[i].refreshFunction,
        this.intervals[i].refreshInterval,
      );
    }
  }

  private getTriggerRouteKey(route: string) {
    return route.substring(route.indexOf('.') + 1);
  }

  private getTriggerSuccessFunction(
    routeKey: string,
    actionId: string | null,
    parameters: Parameters,
  ): SuccessCallback {
    return (data) => {
      this.store.dispatch({
        type: routeKey + '.success',
        id: actionId,
        cacheKey: UrlContext.getActionHash(parameters),
        data,
        parameters,
      });
    };
  }

  private getTriggerErrorFunction(routeKey: string, actionId: string | null, parameters: Parameters): ErrorCallback {
    return (error) => {
      this.store.dispatch({
        type: routeKey + '.error',
        id: actionId,
        cacheKey: UrlContext.getActionHash(parameters),
        error,
        parameters,
      });
    };
  }

  private getBody(body: Body): string | undefined {
    if (body === null || body === undefined) {
      return undefined;
    }

    if (typeof body === 'string' || body instanceof String) {
      return body.toString();
    }

    return JSON.stringify(body);
  }

  private handleFrontasticRedirect(response: Response) {
    if (response.headers.has('Frontastic-Location')) {
      this.router.nextRouter.push(response.headers.get('Frontastic-Location'));
    }
  }

  private getReloadInterval() {
    return 5 * 60 * 1000;
  }
}

export default DirectApi;
